import type {
  AnalyticsSummaryResponse,
  FundamentalsResponse,
  GenerationalResponse,
  HeatmapResponse,
} from '@/api/types';

/**
 * Filtro por dimensión de reporte de la analítica.
 *
 * La dimensión es la misma unidad por la que reporta el backend (`AnalyticsDimensionBuilder`):
 * un comportamiento cuando el fundamental tiene Actitudes y Comportamientos configuradas, y el
 * fundamental cuando no las tiene. Por eso el identificador de una opción del filtro coincide
 * siempre con `DimensionScore.id`, `HeatmapColumn.id` y `GenerationalDimensionAverage.id`, y el
 * filtro puede aplicarse en cliente sobre las respuestas ya descargadas.
 *
 * Todas las funciones devuelven la respuesta original cuando no hay dimensión seleccionada, para
 * que las pestañas consuman siempre la misma forma de datos.
 */

/** Opción seleccionable: un comportamiento, o un fundamental sin comportamientos. */
export interface DimensionOption {
  id: string;
  name: string;
}

/** Opciones agrupadas bajo el fundamental del que cuelgan. */
export interface DimensionOptionGroup {
  fundamentalId: string;
  fundamentalName: string;
  /** true cuando el grupo lista comportamientos; false cuando la única opción es el fundamental. */
  hasBehaviors: boolean;
  options: DimensionOption[];
}

/**
 * Opciones del filtro a partir del catálogo con puntajes. Un fundamental con comportamientos
 * activos aporta un renglón por comportamiento y no aparece él mismo como opción: su promedio
 * mezcla conductas distintas y filtrar por él daría un corte que ninguna pestaña reporta.
 */
export function buildDimensionOptions(
  fundamentals: FundamentalsResponse | undefined,
): DimensionOptionGroup[] {
  return (fundamentals ?? []).map((fundamental) => {
    const hasBehaviors = fundamental.behaviors.length > 0;
    return {
      fundamentalId: fundamental.id,
      fundamentalName: fundamental.name,
      hasBehaviors,
      options: hasBehaviors
        ? [...fundamental.behaviors]
            .sort((first, second) => first.order - second.order)
            .map((behavior) => ({ id: behavior.id, name: behavior.name }))
        : [{ id: fundamental.id, name: fundamental.name }],
    };
  });
}

/** Nombre de la dimensión seleccionada, o `null` si no hay filtro o el id ya no existe. */
export function resolveDimensionName(
  groups: DimensionOptionGroup[],
  dimensionId: string,
): string | null {
  if (!dimensionId) return null;
  for (const group of groups) {
    const match = group.options.find((option) => option.id === dimensionId);
    if (match) return match.name;
  }
  return null;
}

/**
 * Resumen recortado a la dimensión: los rankings dejan solo esa fila, y el promedio general y el
 * número de evaluaciones pasan a ser los suyos. Participación (tasa de respuesta, evaluadores,
 * promedio por día), puntaje por OU y omisiones se conservan tal cual porque el backend no los
 * desglosa por dimensión; la vista lo advierte con `DimensionFilterNotice`.
 */
export function filterSummaryByDimension(
  summary: AnalyticsSummaryResponse | undefined,
  dimensionId: string,
): AnalyticsSummaryResponse | undefined {
  if (!summary || !dimensionId) return summary;

  const selected = summary.dimensions.filter((dimension) => dimension.id === dimensionId);
  const score = selected[0];

  return {
    ...summary,
    totalEvaluations: score?.evaluations ?? 0,
    overallAverage: score?.score ?? 0,
    dimensions: selected,
    topBehaviors: summary.topBehaviors.filter((dimension) => dimension.id === dimensionId),
    opportunities: summary.opportunities.filter((dimension) => dimension.id === dimensionId),
  };
}

/**
 * Mapa de calor con una sola columna. Las filas se recortan por posición porque `scores` está
 * alineado con `columns`; conservar el arreglo completo dejaría los promedios por OU calculados
 * sobre todas las dimensiones.
 */
export function filterHeatmapByDimension(
  heatmap: HeatmapResponse | undefined,
  dimensionId: string,
): HeatmapResponse | undefined {
  if (!heatmap || !dimensionId) return heatmap;

  const columnIndex = heatmap.columns.findIndex((column) => column.id === dimensionId);
  if (columnIndex < 0) {
    return { columns: [], rows: heatmap.rows.map((row) => ({ ...row, scores: [] })) };
  }

  return {
    columns: [heatmap.columns[columnIndex]!],
    rows: heatmap.rows.map((row) => ({ ...row, scores: [row.scores[columnIndex] ?? null] })),
  };
}

/**
 * Bandas generacionales con una sola dimensión. `overallAverage` pasa a ser el promedio de esa
 * dimensión en la banda para que el panorama muestre el puntaje filtrado; el número de personas
 * y de evaluaciones no se desglosa por dimensión y se conserva.
 */
export function filterGenerationalByDimension(
  generational: GenerationalResponse | undefined,
  dimensionId: string,
): GenerationalResponse | undefined {
  if (!generational || !dimensionId) return generational;

  return generational.map((group) => {
    const selected = group.dimensions.filter((dimension) => dimension.id === dimensionId);
    return {
      ...group,
      overallAverage: selected[0]?.average ?? 0,
      dimensions: selected,
    };
  });
}

/**
 * Catálogo con puntajes recortado: solo el fundamental que contiene la dimensión y, dentro de él,
 * solo el comportamiento seleccionado. El encabezado del fundamental conserva su propio promedio
 * —es el contexto de la fila, no el dato filtrado— y por eso no se sobrescribe.
 */
export function filterFundamentalsByDimension(
  fundamentals: FundamentalsResponse | undefined,
  dimensionId: string,
): FundamentalsResponse | undefined {
  if (!fundamentals || !dimensionId) return fundamentals;

  return fundamentals
    .filter((fundamental) =>
      fundamental.id === dimensionId
      || fundamental.behaviors.some((behavior) => behavior.id === dimensionId))
    .map((fundamental) => ({
      ...fundamental,
      behaviors: fundamental.behaviors.filter((behavior) => behavior.id === dimensionId),
    }));
}
